import { wrapApiCall } from '@/lib/axios'
import { clampTotalPages, fetchPagesInBatches } from '@/utils/helper/paged-fetch.helper'
import { satangToBaht } from '@/utils/helper/stang-to-baht.helper'
import type { AxiosInstance } from 'axios'
import {
  ClosedLostListResponse,
  ConversionChannelResponse,
  FollowupStatusResponse,
  LeadGenerationSummaryData,
  MarketingChannelResponse,
  OpportunityAnalysisCaseRow,
  OpportunityAnalysisRowsResponse,
  OpportunityAnalysisValueRow,
  PackageSoldByPackageTypeResponse,
  Pagination,
  RenewalOpportunitiesOverviewData,
  RenewalPackageSummaryRow,
  RenewalSalesPackageSummaryRow,
  SalesBySalePersonAndPackageTypeResponse,
  TotalSalesBySalespersonResponse,
  TotalSalesForAllSalespersons,
} from './model'

const base = '/postgrest'
const EXISTING_CUSTOMER = 'EXISTING_CUSTOMER'
const OPPORTUNITY_ENTITY = 'OPPORTUNITY'

type PostgrestListResponse = {
  data?: Record<string, unknown>[]
  pagination?: Pagination
}

type PostgrestAggregateResponse = Record<string, unknown>

type GetDashboardParams = {
  from: string
  to: string
}

type GetClosedLostListParams = {
  from: string
  to: string
  page: number
  pageSize: number
}

type GetTotalSalesParams = {
  from: string
  to: string
  page: number
  pageSize: number
}

type GetMarketingChannelParams = {
  from: string
  to: string
}

type GetConversionChannelParams = {
  from: string
  to: string
}

type GetPackageSoldByPackageTypeParams = {
  from: string
  to: string
  page: number
  pageSize: number
}

type GetSalesBySalePersonAndPackageTypeParams = {
  from: string
  to: string
  page: number
  pageSize: number
}

type GetOpportunityAnalysisRowsParams = {
  from: string
  to: string
  page: number
  pageSize: number
}

type GetFollowupStatusParams = {
  year: number
  month: number
  page: number
  pageSize: number
}

const toNumeric = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toNullableNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}
const DAY_IN_MS = 24 * 60 * 60 * 1000
const POSTGREST_PAGE_SIZE = 500

const toNullableString = (value: unknown) =>
  typeof value === 'string'
    ? value
    : typeof value === 'number' && Number.isFinite(value)
      ? String(value)
      : typeof value === 'bigint'
        ? value.toString()
        : null

const toDate = (value: unknown) => {
  if (typeof value !== 'string') {
    return null
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const ageInDays = (start: Date | null, end: Date | null) => {
  if (!start || !end) {
    return null
  }

  const diff = end.getTime() - start.getTime()
  if (diff < 0) {
    return null
  }

  return Math.floor(diff / DAY_IN_MS)
}

const getUtcYearMonth = (value: string) => {
  const parsed = new Date(value)
  return {
    year: parsed.getUTCFullYear(),
    month: parsed.getUTCMonth() + 1,
  }
}

const toUtcDateString = (value: string) => {
  const parsed = new Date(value)
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10)
  }

  return value.slice(0, 10)
}

const getAggregateCountFromResponse = (data: Record<string, unknown> | undefined) => {
  const directCount = toNumeric(data?.count)
  if (directCount > 0) {
    return directCount
  }

  const nestedCount = toNumeric(
    (data?.data as Array<Record<string, unknown>> | undefined)?.[0]?.count
  )
  return nestedCount
}

const LEAD_SOURCE_CHANNELS = [
  'FACEBOOK',
  'LINE',
  'GOOGLE',
  'REFERRED',
  'EVENT',
  'BROCHURE',
  'OTHER',
] as const

type LeadSourceChannel = (typeof LEAD_SOURCE_CHANNELS)[number]

type PackageSalesItemSoldByDateRow = {
  sales_user_id: string | null
  sales_code: string | null
  sales_first_name: string | null
  sales_last_name: string | null
  sales_full_name: string | null
  package_type_id: number
  package_type_name_en: string
  package_type_name_th: string
  package_id: number
  package_name: string
  quantity_sold: number
  total_sales_amount: number
}

export const LeadGenerationService = (client: AxiosInstance) => {
  const getSaleStaffIds = async (): Promise<Set<number>> => {
    const { data: roleCategories } = await wrapApiCall(() =>
      client.get<Record<string, number[]>>('/roles/categories')
    )

    const saleRoleIds = (roleCategories?.['sales-owner'] ?? []).filter(
      (id): id is number => typeof id === 'number'
    )

    if (!saleRoleIds.length) {
      return new Set<number>()
    }

    const saleStaffIds = new Set<number>()

    const fetchStaffPage = (page: number) =>
      wrapApiCall(() =>
        client.get<PostgrestListResponse>('/users/staff', {
          params: {
            page,
            pageSize: POSTGREST_PAGE_SIZE,
            roleIds: saleRoleIds.join(','),
            // QTY-2122: the Sales group is CS & Sales role OR the Sale preset,
            // the same roster the Salesperson dropdown lists — without this,
            // preset-only sellers' opportunities never reach the tab.
            includePresets: true,
          },
        })
      )

    // `/users/staff` always returns `pagination.total`, so page 1 tells us
    // the total up front and the remaining pages can be fetched in parallel
    // instead of one-at-a-time.
    const { data: firstData } = await fetchStaffPage(1)
    const firstRows = firstData?.data ?? []
    const total = toNumeric(firstData?.pagination?.total ?? firstRows.length)
    const totalPages = clampTotalPages(
      total,
      POSTGREST_PAGE_SIZE,
      'lead-generation.getSaleStaffIds'
    )

    const restRows =
      totalPages > 1
        ? await fetchPagesInBatches(
            Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
            (page) => fetchStaffPage(page).then(({ data }) => data?.data ?? [])
          )
        : []

    ;[firstRows, ...restRows].flat().forEach((staff) => {
      const id = toNullableNumber(staff.id)
      if (id !== null) {
        saleStaffIds.add(id)
      }
    })

    return saleStaffIds
  }

  return {
    async getOverviewStat({ from, to }: GetDashboardParams): Promise<LeadGenerationSummaryData> {
      const dateFilters = [`created_at=gte.${from}`, `created_at=lt.${to}`]

      const getCount = async (path: string, filters: string[]) => {
        const query = new URLSearchParams()
        query.append('count', 'true')
        filters.forEach((filter) => query.append('filter', filter))

        const { data } = await wrapApiCall(() =>
          client.get<PostgrestAggregateResponse>(`${base}/${path}/aggregate?${query.toString()}`)
        )
        return getAggregateCountFromResponse(data)
      }

      const getLatestMonthlyLeadStatuses = async () => {
        const latestStatusByLeadId = new Map<number, string>()

        const fetchTransitionsPage = (page: number, withCount: boolean) => {
          const query = new URLSearchParams()
          query.append('select', 'id,lead_id,to_status,created_at')
          query.append('page', String(page))
          query.append('pageSize', '500')
          if (withCount) query.append('count', 'true')
          query.append('order', 'created_at.desc,id.desc')
          dateFilters.forEach((filter) => query.append('filter', filter))

          return wrapApiCall(() =>
            client.get<PostgrestListResponse>(`${base}/lead_status_transitions?${query.toString()}`)
          )
        }
        const mapTransitionRows = (data: PostgrestListResponse | undefined) =>
          (data?.data ?? []).map((row) => ({
            leadId: toNumeric(row.lead_id),
            toStatus: toNullableString(row.to_status) ?? '',
          }))

        // Page 1 requests the count so we know the total up front; the
        // remaining pages skip `count=true` (the backend would otherwise
        // re-run the full view just to recompute an unchanging total) and
        // are fetched in parallel instead of sequentially. Rows are then
        // processed in page order (each page is already sorted
        // `created_at.desc,id.desc`, so the concatenated order still walks
        // newest-to-oldest, which matters for "first write wins" below).
        const { data: firstData } = await fetchTransitionsPage(1, true)
        const firstRows = mapTransitionRows(firstData)

        const total = toNumeric(firstData?.pagination?.total ?? firstRows.length)
        const totalPages = clampTotalPages(
          total,
          500,
          'lead-generation.getOverviewStat.getLatestMonthlyLeadStatuses'
        )

        const restRows =
          totalPages > 1
            ? await fetchPagesInBatches(
                Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
                (page) =>
                  fetchTransitionsPage(page, false).then(({ data }) => mapTransitionRows(data))
              )
            : []

        const rows = [firstRows, ...restRows].flat()

        rows.forEach((row) => {
          if (!row.leadId || !row.toStatus || latestStatusByLeadId.has(row.leadId)) {
            return
          }
          latestStatusByLeadId.set(row.leadId, row.toStatus)
        })

        let lost = 0
        let unqualified = 0
        let leadToProspect = 0
        let leadToMember = 0

        latestStatusByLeadId.forEach((status) => {
          if (status === 'CLOSE_LOST') lost += 1
          if (status === 'UNQUALIFIED') unqualified += 1
          if (status === 'PROSPECT') leadToProspect += 1
          if (status === 'MEMBER') leadToMember += 1
        })

        return {
          lost,
          unqualified,
          leadToProspect,
          leadToMember,
        }
      }

      const [lead, latestMonthlyStatuses] = await Promise.all([
        getCount('leads', ['status=eq.LEAD', ...dateFilters]),
        getLatestMonthlyLeadStatuses(),
      ])
      const { lost, unqualified, leadToProspect, leadToMember } = latestMonthlyStatuses

      return {
        topStats: {
          leadGeneration: Number(lead) || 0,
          qualified: Number(leadToProspect) || 0,
          closedWon: Number(leadToMember) || 0,
          closedLost: Number(lost) || 0,
          unqualified: Number(unqualified) || 0,
        },
        conversion: {
          leadToProspectRate:
            Number(lead) === 0
              ? 0
              : Number(((Number(leadToProspect) / Number(lead)) * 100).toFixed(2)),

          prospectToMemberRate:
            Number(leadToProspect) === 0
              ? 0
              : Number(((Number(leadToMember) / Number(leadToProspect)) * 100).toFixed(2)),
        },
      }
    },

    async getRenewalOverviewStat({
      from,
      to,
    }: GetDashboardParams): Promise<RenewalOpportunitiesOverviewData> {
      const saleStaffIds = await getSaleStaffIds()
      if (!saleStaffIds.size) {
        return {
          created: 0,
          open: 0,
          closedWon: 0,
          closedLost: 0,
          totalMembers: 0,
        }
      }

      const saleIdList = Array.from(saleStaffIds).join(',')
      const fromDate = toUtcDateString(from)
      const toDate = toUtcDateString(to)

      const getCount = async (status: 'OPEN_OPPORTUNITY' | 'CLOSE_WON' | 'CLOSE_LOST') => {
        const query = new URLSearchParams()
        query.append('count', 'true')
        query.append('filter', `customer_type=eq.${EXISTING_CUSTOMER}`)
        query.append('filter', `entity_type=eq.${OPPORTUNITY_ENTITY}`)
        query.append('filter', `status_raw=eq.${status}`)
        query.append('filter', `reference_at=gte.${from}`)
        query.append('filter', `reference_at=lt.${to}`)
        query.append('filter', `sales_user_id=in.(${saleIdList})`)

        const { data } = await wrapApiCall(() =>
          client.get<PostgrestAggregateResponse>(
            `${base}/dashboard_opportunity_fact_view/aggregate?${query.toString()}`
          )
        )
        return getAggregateCountFromResponse(data)
      }

      const [open, closedWon, closedLost] = await Promise.all([
        getCount('OPEN_OPPORTUNITY'),
        getCount('CLOSE_WON'),
        getCount('CLOSE_LOST'),
      ])

      const created = open + closedWon + closedLost

      const memberQuery = new URLSearchParams()
      memberQuery.append('count', 'true')
      memberQuery.append('filter', `month_start=gte.${fromDate}`)
      memberQuery.append('filter', `month_start=lt.${toDate}`)
      memberQuery.append('filter', `member_created_at=lt.${toDate}`)
      memberQuery.append('filter', `sale_owner_id=in.(${saleIdList})`)

      const { data: memberData } = await wrapApiCall(() =>
        client.get<PostgrestAggregateResponse & { data?: { count?: number }[] }>(
          `/postgrest/member_monthly_latest_activities/aggregate?${memberQuery.toString()}`
        )
      )
      const totalMembers = toNumeric(memberData?.count ?? memberData?.data?.[0]?.count)

      return {
        created,
        open,
        closedWon,
        closedLost,
        totalMembers,
      }
    },

    async getRenewalPackageSoldByPackageType({
      from,
      to,
      page,
      pageSize,
    }: GetPackageSoldByPackageTypeParams): Promise<PackageSoldByPackageTypeResponse> {
      const saleStaffIds = await getSaleStaffIds()
      if (!saleStaffIds.size) {
        return { data: [] }
      }

      const saleIdList = Array.from(saleStaffIds).join(',')

      const mapRenewalPackageRow = (row: Record<string, unknown>): RenewalPackageSummaryRow => ({
        packageTypeId: toNullableNumber(row.package_type_id),
        packageTypeNameEn: toNullableString(row.package_type_name_en),
        packageTypeNameTh: toNullableString(row.package_type_name_th),
        packageId: toNullableNumber(row.package_id),
        packageName: toNullableString(row.package_name),
        packagePriceThb: toNumeric(row.package_price_thb),
        recordCount: toNumeric(row.record_count),
        expectedClosingValueThb: toNumeric(row.expected_closing_value_thb),
        statusRaw: toNullableString(row.status_raw),
      })

      const fetchRenewalPackagePage = (fetchPage: number, withCount: boolean) => {
        const query = new URLSearchParams()
        query.append(
          'select',
          [
            'package_type_id',
            'package_type_name_en',
            'package_type_name_th',
            'package_id',
            'package_name',
            'package_price_thb',
            'record_count',
            'expected_closing_value_thb',
            'status_raw',
          ].join(',')
        )
        query.append('page', String(fetchPage))
        query.append('pageSize', String(pageSize))
        if (withCount) query.append('count', 'true')
        query.append('order', 'expected_closing_value_thb.desc')
        query.append('filter', `customer_type=eq.${EXISTING_CUSTOMER}`)
        query.append('filter', `entity_type=eq.${OPPORTUNITY_ENTITY}`)
        query.append('filter', 'status_raw=eq.OPEN_OPPORTUNITY')
        query.append('filter', `expected_closed_at=gte.${from}`)
        query.append('filter', `expected_closed_at=lt.${to}`)
        query.append('filter', `sales_user_id=in.(${saleIdList})`)

        return wrapApiCall(() =>
          client.get<PostgrestListResponse>(
            `${base}/dashboard_opportunity_sales_package_summary_view?${query.toString()}`
          )
        )
      }

      // The first fetched page (the caller-supplied `page`, same as before)
      // requests the count so we know the total up front; the remaining
      // pages through the last page skip `count=true` (the backend would
      // otherwise re-run the full view just to recompute an unchanging
      // total) and are fetched in parallel instead of sequentially.
      const { data: firstRenewalPackageData } = await fetchRenewalPackagePage(page, true)
      const firstRenewalPackageRows = (firstRenewalPackageData?.data ?? []).map(
        mapRenewalPackageRow
      )

      const renewalPackageTotal = toNumeric(
        firstRenewalPackageData?.pagination?.total ?? firstRenewalPackageRows.length
      )
      const renewalPackageTotalPages = clampTotalPages(
        renewalPackageTotal,
        pageSize,
        'lead-generation.getRenewalPackageSoldByPackageType'
      )

      const restRenewalPackageRows =
        renewalPackageTotalPages > page
          ? await fetchPagesInBatches(
              Array.from(
                { length: renewalPackageTotalPages - page },
                (_, index) => page + index + 1
              ),
              (pageNumber) =>
                fetchRenewalPackagePage(pageNumber, false).then(({ data }) =>
                  (data?.data ?? []).map(mapRenewalPackageRow)
                )
            )
          : []

      const allRows: RenewalPackageSummaryRow[] = [
        firstRenewalPackageRows,
        ...restRenewalPackageRows,
      ].flat()

      const grouped = new Map<
        string,
        {
          package_type_id: number
          package_type_name_en: string
          package_type_name_th: string
          package_id: number
          package_name: string
          package_price_thb: number
          quantity_sold: number
          total_sales_amount: number
        }
      >()

      for (const row of allRows) {
        if (!row.packageId || !row.packageTypeId) {
          continue
        }

        const key = `${row.packageTypeId}-${row.packageId}`
        const fallbackUnitPrice =
          row.recordCount > 0 ? row.expectedClosingValueThb / row.recordCount : 0
        const packagePriceThb = row.packagePriceThb || fallbackUnitPrice

        if (!grouped.has(key)) {
          grouped.set(key, {
            package_type_id: row.packageTypeId,
            package_type_name_en: row.packageTypeNameEn ?? '-',
            package_type_name_th: row.packageTypeNameTh ?? '-',
            package_id: row.packageId,
            package_name: row.packageName ?? '-',
            package_price_thb: packagePriceThb,
            quantity_sold: 0,
            total_sales_amount: 0,
          })
        }

        const current = grouped.get(key)!
        if (!current.package_price_thb && packagePriceThb > 0) {
          current.package_price_thb = packagePriceThb
        }
        current.quantity_sold += row.recordCount
        current.total_sales_amount += row.expectedClosingValueThb
      }

      return {
        data: Array.from(grouped.values())
          .sort((a, b) => b.total_sales_amount - a.total_sales_amount)
          .map((item) => ({
            ...item,
            package_price_thb: String(item.package_price_thb),
            quantity_sold: String(item.quantity_sold),
            total_sales_amount: String(item.total_sales_amount),
          })),
      }
    },

    async getRenewalSalesBySalePersonAndPackageType({
      from,
      to,
      page,
      pageSize,
    }: GetSalesBySalePersonAndPackageTypeParams): Promise<SalesBySalePersonAndPackageTypeResponse> {
      const saleStaffIds = await getSaleStaffIds()
      if (!saleStaffIds.size) {
        return { data: [] }
      }

      const saleIdList = Array.from(saleStaffIds).join(',')
      const selectedFromDate = new Date(from)
      const lowerBound = selectedFromDate

      const mapRenewalSalesRow = (row: Record<string, unknown>): RenewalSalesPackageSummaryRow => ({
        salesUserId: toNullableString(row.sales_user_id),
        salesCode: toNullableString(row.sales_code),
        salesFirstName: toNullableString(row.sales_first_name),
        salesLastName: toNullableString(row.sales_last_name),
        salesFullName: toNullableString(row.sales_full_name),
        statusRaw: toNullableString(row.status_raw),
        packageTypeId: toNullableNumber(row.package_type_id),
        packageTypeNameEn: toNullableString(row.package_type_name_en),
        packageTypeNameTh: toNullableString(row.package_type_name_th),
        packageId: toNullableNumber(row.package_id),
        packageName: toNullableString(row.package_name),
        packagePriceThb: toNumeric(row.package_price_thb),
        recordCount: toNumeric(row.record_count),
        totalUnitPriceThb: toNumeric(row.total_unit_price_thb),
        avgOpportunityPercent: toNumeric(row.avg_opportunity_percent),
        expectedClosingValueThb: toNumeric(row.expected_closing_value_thb),
      })

      const fetchRenewalSalesPage = (fetchPage: number, withCount: boolean) => {
        const query = new URLSearchParams()
        query.append(
          'select',
          [
            'sales_user_id',
            'sales_code',
            'sales_first_name',
            'sales_last_name',
            'sales_full_name',
            'status_raw',
            'package_type_id',
            'package_type_name_en',
            'package_type_name_th',
            'package_id',
            'package_name',
            'package_price_thb',
            'record_count',
            'total_unit_price_thb',
            'avg_opportunity_percent',
            'expected_closing_value_thb',
          ].join(',')
        )
        query.append('page', String(fetchPage))
        query.append('pageSize', String(pageSize))
        if (withCount) query.append('count', 'true')
        query.append('order', 'expected_closing_value_thb.desc')
        query.append('filter', `customer_type=eq.${EXISTING_CUSTOMER}`)
        query.append('filter', `entity_type=eq.${OPPORTUNITY_ENTITY}`)
        query.append('filter', 'status_raw=eq.OPEN_OPPORTUNITY')
        query.append('filter', `expected_closed_at=gte.${lowerBound.toISOString()}`)
        query.append('filter', `expected_closed_at=lt.${to}`)
        query.append('filter', `sales_user_id=in.(${saleIdList})`)

        return wrapApiCall(() =>
          client.get<PostgrestListResponse>(
            `${base}/dashboard_opportunity_sales_package_summary_view?${query.toString()}`
          )
        )
      }

      // The first fetched page (the caller-supplied `page`, same as before)
      // requests the count so we know the total up front; the remaining
      // pages through the last page skip `count=true` (the backend would
      // otherwise re-run the full view just to recompute an unchanging
      // total) and are fetched in parallel instead of sequentially.
      const { data: firstRenewalSalesData } = await fetchRenewalSalesPage(page, true)
      const firstRenewalSalesRows = (firstRenewalSalesData?.data ?? []).map(mapRenewalSalesRow)

      const renewalSalesTotal = toNumeric(
        firstRenewalSalesData?.pagination?.total ?? firstRenewalSalesRows.length
      )
      const renewalSalesTotalPages = clampTotalPages(
        renewalSalesTotal,
        pageSize,
        'lead-generation.getRenewalSalesBySalePersonAndPackageType'
      )

      const restRenewalSalesRows =
        renewalSalesTotalPages > page
          ? await fetchPagesInBatches(
              Array.from({ length: renewalSalesTotalPages - page }, (_, index) => page + index + 1),
              (pageNumber) =>
                fetchRenewalSalesPage(pageNumber, false).then(({ data }) =>
                  (data?.data ?? []).map(mapRenewalSalesRow)
                )
            )
          : []

      const allRows: RenewalSalesPackageSummaryRow[] = [
        firstRenewalSalesRows,
        ...restRenewalSalesRows,
      ].flat()

      const grouped = new Map<
        string,
        {
          sales_user_id: string
          sales_code: string
          sales_first_name: string
          sales_last_name: string
          sales_full_name: string
          package_type_id: number
          package_type_name_en: string
          package_type_name_th: string
          package_id: number
          package_name: string
          package_price_thb: number
          quantity_sold: number
          opportunity_sum: number
          total_unit_price_thb: number
          expected_closing_value_thb: number
          total_sales_amount: number
        }
      >()

      for (const row of allRows) {
        if (!row.packageId || !row.packageTypeId) {
          continue
        }

        const salesUserId = row.salesUserId ?? ''
        const salesIdentifier =
          row.salesUserId ?? row.salesCode ?? row.salesFullName ?? 'UNASSIGNED'
        const key = `${salesIdentifier}-${row.packageTypeId}-${row.packageId}`
        if (!grouped.has(key)) {
          grouped.set(key, {
            sales_user_id: salesUserId,
            sales_code: row.salesCode ?? '',
            sales_first_name: row.salesFirstName ?? '',
            sales_last_name: row.salesLastName ?? '',
            sales_full_name: row.salesFullName ?? '',
            package_type_id: row.packageTypeId,
            package_type_name_en: row.packageTypeNameEn ?? '-',
            package_type_name_th: row.packageTypeNameTh ?? '-',
            package_id: row.packageId,
            package_name: row.packageName ?? '-',
            package_price_thb: row.packagePriceThb,
            quantity_sold: 0,
            opportunity_sum: 0,
            total_unit_price_thb: 0,
            expected_closing_value_thb: 0,
            total_sales_amount: 0,
          })
        }

        const current = grouped.get(key)!
        if (!current.package_price_thb && row.packagePriceThb > 0) {
          current.package_price_thb = row.packagePriceThb
        }
        current.quantity_sold += row.recordCount
        current.opportunity_sum += row.avgOpportunityPercent * row.recordCount
        current.total_unit_price_thb += row.totalUnitPriceThb
        current.expected_closing_value_thb += row.expectedClosingValueThb
        current.total_sales_amount += row.expectedClosingValueThb
      }

      return {
        data: Array.from(grouped.values())
          .sort((a, b) => b.total_sales_amount - a.total_sales_amount)
          .map((item) => ({
            ...item,
            package_price_thb: String(item.package_price_thb),
            quantity_sold: String(item.quantity_sold),
            total_unit_price_thb: String(item.total_unit_price_thb),
            avg_opportunity_percent: String(
              item.quantity_sold > 0 ? item.opportunity_sum / item.quantity_sold : 0
            ),
            expected_closing_value_thb: String(item.expected_closing_value_thb),
            total_sales_amount: String(item.total_sales_amount),
          })),
      }
    },

    async getOpportunityAnalysisRows({
      from,
      to,
      page,
      pageSize,
    }: GetOpportunityAnalysisRowsParams): Promise<OpportunityAnalysisRowsResponse> {
      const { year: fromYear, month: fromMonth } = getUtcYearMonth(from)
      const { year: toYear, month: toMonth } = getUtcYearMonth(to)
      const fromMonthKey = `${fromYear}-${String(fromMonth).padStart(2, '0')}`
      const toMonthKey = `${toYear}-${String(toMonth).padStart(2, '0')}`

      const fetchValueRows = async () => {
        const mapRow = (row: Record<string, unknown>): OpportunityAnalysisValueRow => ({
          monthLocal: toNumeric(row.month_local),
          salesUserId: toNullableString(row.sales_user_id) ?? '',
          salesCode: toNullableString(row.sales_code) ?? '',
          salesFullName: toNullableString(row.sales_full_name) ?? '',
          entityType: toNullableString(row.entity_type) ?? '',
          customerType: toNullableString(row.customer_type) ?? '',
          packageTypeNameEn: toNullableString(row.package_type_name_en) ?? '',
          expectedClosingValueThb: toNumeric(row.expected_closing_value_thb),
          closedWonValueThb: satangToBaht(toNumeric(row.closed_won_value_thb)),
          closedLostValueThb: toNumeric(row.closed_lost_value_thb),
        })

        const fetchPage = (pageNumber: number, withCount: boolean) => {
          const query = new URLSearchParams()
          query.append(
            'select',
            [
              'month_local',
              'sales_user_id',
              'sales_code',
              'sales_full_name',
              'entity_type',
              'customer_type',
              'package_type_name_en',
              'expected_closing_value_thb',
              'closed_won_value_thb',
              'closed_lost_value_thb',
            ].join(',')
          )
          query.append('page', String(pageNumber))
          query.append('pageSize', String(pageSize))
          if (withCount) query.append('count', 'true')
          query.append('order', 'expected_closing_value_thb.desc')
          query.append('filter', `month_key=gte.${fromMonthKey}`)
          query.append('filter', `month_key=lt.${toMonthKey}`)

          return wrapApiCall(() =>
            client.get<PostgrestListResponse>(
              `${base}/dashboard_opportunity_analysis_value_summary_view?${query.toString()}`
            )
          )
        }

        // The first fetched page (the caller-supplied `page`, same as
        // before) requests the count so we know the total up front; the
        // remaining pages through the last page skip `count=true` (the
        // backend would otherwise re-run the full view just to recompute an
        // unchanging total) and are fetched in parallel instead of
        // sequentially.
        const { data: firstData } = await fetchPage(page, true)
        const firstRows = (firstData?.data ?? []).map(mapRow)

        const total = toNumeric(firstData?.pagination?.total ?? firstRows.length)
        const totalPages = clampTotalPages(
          total,
          pageSize,
          'lead-generation.getOpportunityAnalysisRows.fetchValueRows'
        )

        const restRows =
          totalPages > page
            ? await fetchPagesInBatches(
                Array.from({ length: totalPages - page }, (_, index) => page + index + 1),
                (pageNumber) =>
                  fetchPage(pageNumber, false).then(({ data }) => (data?.data ?? []).map(mapRow))
              )
            : []

        return [firstRows, ...restRows].flat()
      }

      const fetchCaseRows = async () => {
        const mapRow = (row: Record<string, unknown>): OpportunityAnalysisCaseRow => ({
          monthLocal: toNumeric(row.month_local),
          salesUserId: toNullableString(row.sales_user_id) ?? '',
          salesCode: toNullableString(row.sales_code) ?? '',
          salesFullName: toNullableString(row.sales_full_name) ?? '',
          entityType: toNullableString(row.entity_type) ?? '',
          customerType: toNullableString(row.customer_type) ?? '',
          packageTypeScope: toNullableString(row.package_type_scope) ?? '',
          packageTypeNameEn: toNullableString(row.package_type_name_en) ?? '',
          expectedCaseQty: toNumeric(row.expected_case_qty),
          closedWonCaseQty: toNumeric(row.closed_won_case_qty),
          closedLostCaseQty: toNumeric(row.closed_lost_case_qty),
          openedOpportunityQty: toNumeric(row.opened_opportunity_qty),
          qualifiedLeadQty: toNumeric(row.qualified_lead_qty),
          unqualifiedLeadQty: toNumeric(row.unqualified_lead_qty),
        })

        const fetchPage = (pageNumber: number, withCount: boolean) => {
          const query = new URLSearchParams()
          query.append(
            'select',
            [
              'month_local',
              'sales_user_id',
              'sales_code',
              'sales_full_name',
              'entity_type',
              'customer_type',
              'package_type_scope',
              'package_type_name_en',
              'expected_case_qty',
              'closed_won_case_qty',
              'closed_lost_case_qty',
              'opened_opportunity_qty',
              'qualified_lead_qty',
              'unqualified_lead_qty',
            ].join(',')
          )
          query.append('page', String(pageNumber))
          query.append('pageSize', String(pageSize))
          if (withCount) query.append('count', 'true')
          query.append('order', 'closed_won_case_qty.desc')
          query.append('filter', `month_key=gte.${fromMonthKey}`)
          query.append('filter', `month_key=lt.${toMonthKey}`)

          return wrapApiCall(() =>
            client.get<PostgrestListResponse>(
              `${base}/dashboard_opportunity_analysis_case_summary_view?${query.toString()}`
            )
          )
        }

        // The first fetched page (the caller-supplied `page`, same as
        // before) requests the count so we know the total up front; the
        // remaining pages through the last page skip `count=true` (the
        // backend would otherwise re-run the full view just to recompute an
        // unchanging total) and are fetched in parallel instead of
        // sequentially.
        const { data: firstData } = await fetchPage(page, true)
        const firstRows = (firstData?.data ?? []).map(mapRow)

        const total = toNumeric(firstData?.pagination?.total ?? firstRows.length)
        const totalPages = clampTotalPages(
          total,
          pageSize,
          'lead-generation.getOpportunityAnalysisRows.fetchCaseRows'
        )

        const restRows =
          totalPages > page
            ? await fetchPagesInBatches(
                Array.from({ length: totalPages - page }, (_, index) => page + index + 1),
                (pageNumber) =>
                  fetchPage(pageNumber, false).then(({ data }) => (data?.data ?? []).map(mapRow))
              )
            : []

        return [firstRows, ...restRows].flat()
      }

      const [valueData, caseData] = await Promise.all([fetchValueRows(), fetchCaseRows()])

      return { valueData, caseData }
    },

    async getClosedLostList({
      from,
      to,
      page,
      pageSize,
    }: GetClosedLostListParams): Promise<ClosedLostListResponse> {
      const query = new URLSearchParams()
      query.append(
        'select',
        'id,contact_name,close_lost_reason,sales_full_name,created_at,closed_at'
      )
      query.append('order', 'closed_at.desc')
      query.append('page', String(page))
      query.append('pageSize', String(pageSize))
      query.append('filter', 'status=eq.CLOSE_LOST')
      query.append('filter', `closed_at=gte.${from}`)
      query.append('filter', `closed_at=lt.${to}`)

      const { data } = await wrapApiCall(() =>
        client.get<PostgrestListResponse>(`${base}/leads_with_sales_view?${query.toString()}`)
      )

      const rows = (data?.data ?? [])
        .map((row) => ({
          id: toNumeric(row.id),
          contactName: toNullableString(row.contact_name) ?? '',
          closeLostReason: toNullableString(row.close_lost_reason),
          salesFullName: toNullableString(row.sales_full_name),
          createdAt: toDate(row.created_at),
          closedAt: toDate(row.closed_at),
        }))
        .filter((row) => row.id > 0)

      const leadIds = rows.map((row) => row.id)
      const leadDatesByLeadId = new Map<number, Date[]>()
      const closeLostDatesByLeadId = new Map<number, Date>()

      if (leadIds.length) {
        const transitionQuery = new URLSearchParams()
        transitionQuery.append('select', 'lead_id,to_status,created_at')
        transitionQuery.append('order', 'created_at.asc')
        transitionQuery.append('filter', `lead_id=in.(${leadIds.join(',')})`)
        transitionQuery.append('filter', 'to_status=in.(LEAD,CLOSE_LOST)')

        const { data: transitionData } = await wrapApiCall(() =>
          client.get<PostgrestListResponse>(
            `${base}/lead_status_transitions?${transitionQuery.toString()}`
          )
        )

        const transitionRows = transitionData?.data ?? []

        for (const row of transitionRows) {
          const leadId = toNumeric(row.lead_id)
          const toStatus = toNullableString(row.to_status)
          const changedAt = toDate(row.created_at)

          if (!leadId || !toStatus || !changedAt) {
            continue
          }

          if (toStatus === 'LEAD') {
            const existingLeadDates = leadDatesByLeadId.get(leadId) ?? []
            existingLeadDates.push(changedAt)
            leadDatesByLeadId.set(leadId, existingLeadDates)
          }

          if (toStatus === 'CLOSE_LOST') {
            const existingCloseLostAt = closeLostDatesByLeadId.get(leadId)
            if (!existingCloseLostAt || changedAt > existingCloseLostAt) {
              closeLostDatesByLeadId.set(leadId, changedAt)
            }
          }
        }
      }

      return {
        data: rows.map((row) => {
          const closeLostAt = closeLostDatesByLeadId.get(row.id) ?? row.closedAt
          const leadDates = leadDatesByLeadId.get(row.id) ?? []
          const leadAtFromTransitions = leadDates
            .filter((date) => !closeLostAt || date <= closeLostAt)
            .reduce<Date | null>((latest, date) => {
              if (!latest || date > latest) {
                return date
              }
              return latest
            }, null)

          return {
            id: row.id,
            ageOfLead: ageInDays(leadAtFromTransitions ?? row.createdAt, closeLostAt),
            contact_name: row.contactName,
            close_lost_reason: row.closeLostReason,
            sales_full_name: row.salesFullName,
          }
        }),
        pagination: data?.pagination ?? {
          total: rows.length,
          page,
          pageSize,
        },
      }
    },

    async getTotalSales({
      from,
      page,
      pageSize,
    }: GetTotalSalesParams): Promise<TotalSalesBySalespersonResponse> {
      type SalesFactRow = {
        sellerUserId: string | null
        sellerCode: string | null
        sellerFullName: string | null
        sellerRoleCode: string | null
        sellerRoleName: string | null
        sellerRoleGroup: string | null
        sellerHasSalePreset: boolean
        lineTotalThb: number
      }

      const { year, month } = getUtcYearMonth(from)
      const monthKey = `${year}-${String(month).padStart(2, '0')}`

      const mapSalesFactRow = (row: Record<string, unknown>): SalesFactRow => ({
        sellerUserId: toNullableString(row.seller_user_id),
        sellerCode: toNullableString(row.seller_code),
        sellerFullName: toNullableString(row.seller_full_name),
        sellerRoleCode: toNullableString(row.seller_role_code),
        sellerRoleName: toNullableString(row.seller_role_name),
        sellerRoleGroup: toNullableString(row.seller_role_group),
        sellerHasSalePreset: row.seller_has_sale_preset === true,
        lineTotalThb: satangToBaht(toNumeric(row.line_total_thb)),
      })

      const fetchSalesFactPage = (fetchPage: number, withCount: boolean) => {
        const query = new URLSearchParams()
        query.append(
          'select',
          [
            'seller_user_id',
            'seller_code',
            'seller_full_name',
            'seller_role_code',
            'seller_role_name',
            'seller_role_group',
            'seller_has_sale_preset',
            'line_total_thb',
          ].join(',')
        )
        query.append('page', String(fetchPage))
        query.append('pageSize', '500')
        if (withCount) query.append('count', 'true')
        query.append('filter', `sold_month_key=eq.${monthKey}`)
        query.append('filter', 'item_type=in.(PACKAGE,PRODUCT)')

        return wrapApiCall(() =>
          client.get<PostgrestListResponse>(`${base}/sales_fact_view?${query.toString()}`)
        )
      }

      // Page 1 requests the count so we know the total up front; the
      // remaining pages skip `count=true` (the backend would otherwise
      // re-run the full view just to recompute an unchanging total) and are
      // fetched in parallel instead of sequentially, since the total is
      // already known.
      const { data: firstSalesFactData } = await fetchSalesFactPage(1, true)
      const firstSalesFactRows = (firstSalesFactData?.data ?? []).map(mapSalesFactRow)

      const salesFactTotal = toNumeric(
        firstSalesFactData?.pagination?.total ?? firstSalesFactRows.length
      )
      const salesFactTotalPages = clampTotalPages(
        salesFactTotal,
        500,
        'lead-generation.getTotalSales'
      )

      const restSalesFactRows =
        salesFactTotalPages > 1
          ? await fetchPagesInBatches(
              Array.from({ length: salesFactTotalPages - 1 }, (_, index) => index + 2),
              (page) =>
                fetchSalesFactPage(page, false).then(({ data }) =>
                  (data?.data ?? []).map(mapSalesFactRow)
                )
            )
          : []

      const allRows: SalesFactRow[] = [firstSalesFactRows, ...restSalesFactRows].flat()

      const isSaleUser = (row: SalesFactRow) => {
        const roleGroup = (row.sellerRoleGroup ?? '').toUpperCase()
        const roleCode = (row.sellerRoleCode ?? '').toLowerCase()
        const roleName = (row.sellerRoleName ?? '').toLowerCase()

        // QTY-2017: staff whose primary role is not Sale can still hold the
        // 'sale' preset as a secondary role, and their sales belong here.
        return (
          row.sellerHasSalePreset ||
          roleGroup === 'SALE' ||
          roleCode.includes('sale') ||
          roleName.includes('sale')
        )
      }

      const grouped = new Map<
        string,
        {
          sales_user_id: string
          sales_code: string
          sales_full_name: string
          total_sales: number
        }
      >()

      for (const row of allRows) {
        if (!row.sellerUserId || !isSaleUser(row)) {
          continue
        }

        const key = row.sellerUserId
        if (!grouped.has(key)) {
          grouped.set(key, {
            sales_user_id: row.sellerUserId,
            sales_code: row.sellerCode ?? '-',
            sales_full_name: row.sellerFullName ?? row.sellerCode ?? '-',
            total_sales: 0,
          })
        }

        const current = grouped.get(key)!
        current.total_sales += row.lineTotalThb
      }

      const sortedRows: TotalSalesForAllSalespersons = Array.from(grouped.values()).sort(
        (a, b) => b.total_sales - a.total_sales
      )
      const start = (page - 1) * pageSize
      const paginatedRows = sortedRows.slice(start, start + pageSize)

      return {
        data: paginatedRows,
        pagination: {
          total: sortedRows.length,
          page,
          pageSize,
        },
      }
    },

    async getMarketingChannel({
      from,
      to,
    }: GetMarketingChannelParams): Promise<MarketingChannelResponse> {
      const getCountByChannel = async (
        sourceChannel: LeadSourceChannel | 'UNKNOWN',
        status?: 'MEMBER'
      ) => {
        const query = new URLSearchParams()
        query.append('count', 'true')
        query.append('filter', `created_at=gte.${from}`)
        query.append('filter', `created_at=lt.${to}`)
        if (status) {
          query.append('filter', `status=eq.${status}`)
        }

        if (sourceChannel === 'UNKNOWN') {
          query.append('filter', 'source_channel=is.null')
        } else {
          query.append('filter', `source_channel=eq.${sourceChannel}`)
        }

        const { data } = await wrapApiCall(() =>
          client.get<PostgrestAggregateResponse>(
            `${base}/leads_with_sales_view/aggregate?${query.toString()}`
          )
        )

        return getAggregateCountFromResponse(data)
      }

      const counts = await Promise.all(
        [...LEAD_SOURCE_CHANNELS, 'UNKNOWN' as const].map(async (channel) => ({
          source_channel: channel,
          count: await getCountByChannel(channel),
        }))
      )

      return {
        data: counts.filter((item) => item.count > 0),
      }
    },

    async getConversionChannel({
      from,
      to,
    }: GetConversionChannelParams): Promise<ConversionChannelResponse> {
      const getCountByChannel = async (sourceChannel: LeadSourceChannel | 'UNKNOWN') => {
        const query = new URLSearchParams()
        query.append('count', 'true')
        query.append('filter', `member_created_at=gte.${from}`)
        query.append('filter', `member_created_at=lt.${to}`)

        if (sourceChannel === 'UNKNOWN') {
          query.append('filter', 'referral_source=is.null')
        } else {
          query.append('filter', `referral_source=eq.${sourceChannel}`)
        }

        const { data } = await wrapApiCall(() =>
          client.get<PostgrestAggregateResponse>(
            `${base}/member_monthly_latest_activities/aggregate?${query.toString()}`
          )
        )

        return getAggregateCountFromResponse(data)
      }

      const counts = await Promise.all(
        [...LEAD_SOURCE_CHANNELS, 'UNKNOWN' as const].map(async (channel) => ({
          source_channel: channel,
          count: await getCountByChannel(channel),
        }))
      )

      return {
        data: counts.filter((item) => item.count > 0),
      }
    },

    async getPackageSoldByPackageType({
      from,
      to,
      page,
      pageSize,
    }: GetPackageSoldByPackageTypeParams): Promise<PackageSoldByPackageTypeResponse> {
      const fromDate = toUtcDateString(from)
      const toDate = toUtcDateString(to)

      const mapPackageSalesRow = (row: Record<string, unknown>): PackageSalesItemSoldByDateRow => ({
        sales_user_id: toNullableString(row.sales_user_id),
        sales_code: toNullableString(row.sales_code),
        sales_first_name: toNullableString(row.sales_first_name),
        sales_last_name: toNullableString(row.sales_last_name),
        sales_full_name: toNullableString(row.sales_full_name),
        package_type_id: toNumeric(row.package_type_id),
        package_type_name_en: toNullableString(row.package_type_name_en) ?? '-',
        package_type_name_th: toNullableString(row.package_type_name_th) ?? '-',
        package_id: toNumeric(row.package_id),
        package_name: toNullableString(row.package_name) ?? '-',
        quantity_sold: toNumeric(row.quantity_sold),
        // total_sales_amount is sales_fact_view.full_amount_thb in satang; → baht
        total_sales_amount: satangToBaht(toNumeric(row.total_sales_amount)),
      })

      const fetchPackageSalesPage = (fetchPage: number, withCount: boolean) => {
        const query = new URLSearchParams()
        query.append(
          'select',
          [
            'package_type_id',
            'package_type_name_en',
            'package_type_name_th',
            'package_id',
            'package_name',
            'quantity_sold',
            'total_sales_amount',
          ].join(',')
        )
        query.append('page', String(fetchPage))
        query.append('pageSize', String(POSTGREST_PAGE_SIZE))
        if (withCount) query.append('count', 'true')
        query.append('filter', `sold_date_local=gte.${fromDate}`)
        query.append('filter', `sold_date_local=lt.${toDate}`)

        return wrapApiCall(() =>
          client.get<PostgrestListResponse>(
            `${base}/package_sales_item_sold_by_date_view?${query.toString()}`
          )
        )
      }

      // Page 1 requests the count so we know the total up front; the
      // remaining pages skip `count=true` (the backend would otherwise
      // re-run the full view just to recompute an unchanging total) and are
      // fetched in parallel instead of sequentially, since the total is
      // already known.
      const { data: firstPackageSalesData } = await fetchPackageSalesPage(1, true)
      const firstPackageSalesRows = (firstPackageSalesData?.data ?? []).map(mapPackageSalesRow)

      const packageSalesTotal = toNumeric(
        firstPackageSalesData?.pagination?.total ?? firstPackageSalesRows.length
      )
      const packageSalesTotalPages = clampTotalPages(
        packageSalesTotal,
        POSTGREST_PAGE_SIZE,
        'lead-generation.getPackageSoldByPackageType'
      )

      const restPackageSalesRows =
        packageSalesTotalPages > 1
          ? await fetchPagesInBatches(
              Array.from({ length: packageSalesTotalPages - 1 }, (_, index) => index + 2),
              (page) =>
                fetchPackageSalesPage(page, false).then(({ data }) =>
                  (data?.data ?? []).map(mapPackageSalesRow)
                )
            )
          : []

      const allRows: PackageSalesItemSoldByDateRow[] = [
        firstPackageSalesRows,
        ...restPackageSalesRows,
      ].flat()

      const grouped = new Map<
        string,
        {
          package_type_id: number
          package_type_name_en: string
          package_type_name_th: string
          package_id: number
          package_name: string
          quantity_sold: number
          total_sales_amount: number
        }
      >()

      for (const row of allRows) {
        if (!row.package_id || !row.package_type_id) {
          continue
        }

        const key = `${row.package_type_id}-${row.package_id}`
        if (!grouped.has(key)) {
          grouped.set(key, {
            package_type_id: row.package_type_id,
            package_type_name_en: row.package_type_name_en,
            package_type_name_th: row.package_type_name_th,
            package_id: row.package_id,
            package_name: row.package_name,
            quantity_sold: 0,
            total_sales_amount: 0,
          })
        }

        const current = grouped.get(key)!
        current.quantity_sold += row.quantity_sold
        current.total_sales_amount += row.total_sales_amount
      }

      const aggregated = Array.from(grouped.values())
        .sort((a, b) => b.total_sales_amount - a.total_sales_amount)
        .map((item) => ({
          ...item,
          package_price_thb: String(
            item.quantity_sold > 0 ? item.total_sales_amount / item.quantity_sold : 0
          ),
          quantity_sold: String(item.quantity_sold),
          total_sales_amount: String(item.total_sales_amount),
        }))

      const start = Math.max(0, (page - 1) * pageSize)
      const end = start + pageSize

      return {
        data: aggregated.slice(start, end),
      }
    },

    async getSalesBySalePersonAndPackageType({
      from,
      to,
      page,
      pageSize,
    }: GetSalesBySalePersonAndPackageTypeParams): Promise<SalesBySalePersonAndPackageTypeResponse> {
      const fromDate = toUtcDateString(from)
      const toDate = toUtcDateString(to)

      const mapPackageSalesRow = (row: Record<string, unknown>): PackageSalesItemSoldByDateRow => ({
        sales_user_id: toNullableString(row.sales_user_id),
        sales_code: toNullableString(row.sales_code),
        sales_first_name: toNullableString(row.sales_first_name),
        sales_last_name: toNullableString(row.sales_last_name),
        sales_full_name: toNullableString(row.sales_full_name),
        package_type_id: toNumeric(row.package_type_id),
        package_type_name_en: toNullableString(row.package_type_name_en) ?? '-',
        package_type_name_th: toNullableString(row.package_type_name_th) ?? '-',
        package_id: toNumeric(row.package_id),
        package_name: toNullableString(row.package_name) ?? '-',
        quantity_sold: toNumeric(row.quantity_sold),
        // total_sales_amount is sales_fact_view.full_amount_thb in satang; → baht
        total_sales_amount: satangToBaht(toNumeric(row.total_sales_amount)),
      })

      const fetchPackageSalesPage = (fetchPage: number, withCount: boolean) => {
        const query = new URLSearchParams()
        query.append(
          'select',
          [
            'sales_user_id',
            'sales_code',
            'sales_first_name',
            'sales_last_name',
            'sales_full_name',
            'package_type_id',
            'package_type_name_en',
            'package_type_name_th',
            'package_id',
            'package_name',
            'quantity_sold',
            'total_sales_amount',
          ].join(',')
        )
        query.append('page', String(fetchPage))
        query.append('pageSize', String(POSTGREST_PAGE_SIZE))
        if (withCount) query.append('count', 'true')
        query.append('filter', `sold_date_local=gte.${fromDate}`)
        query.append('filter', `sold_date_local=lt.${toDate}`)

        return wrapApiCall(() =>
          client.get<PostgrestListResponse>(
            `${base}/package_sales_item_sold_by_date_view?${query.toString()}`
          )
        )
      }

      // Page 1 requests the count so we know the total up front; the
      // remaining pages skip `count=true` (the backend would otherwise
      // re-run the full view just to recompute an unchanging total) and are
      // fetched in parallel instead of sequentially, since the total is
      // already known.
      const { data: firstPackageSalesData } = await fetchPackageSalesPage(1, true)
      const firstPackageSalesRows = (firstPackageSalesData?.data ?? []).map(mapPackageSalesRow)

      const packageSalesTotal = toNumeric(
        firstPackageSalesData?.pagination?.total ?? firstPackageSalesRows.length
      )
      const packageSalesTotalPages = clampTotalPages(
        packageSalesTotal,
        POSTGREST_PAGE_SIZE,
        'lead-generation.getSalesBySalePersonAndPackageType'
      )

      const restPackageSalesRows =
        packageSalesTotalPages > 1
          ? await fetchPagesInBatches(
              Array.from({ length: packageSalesTotalPages - 1 }, (_, index) => index + 2),
              (page) =>
                fetchPackageSalesPage(page, false).then(({ data }) =>
                  (data?.data ?? []).map(mapPackageSalesRow)
                )
            )
          : []

      const allRows: PackageSalesItemSoldByDateRow[] = [
        firstPackageSalesRows,
        ...restPackageSalesRows,
      ].flat()

      const grouped = new Map<
        string,
        {
          sales_user_id: string
          sales_code: string
          sales_first_name: string
          sales_last_name: string
          sales_full_name: string
          package_type_id: number
          package_type_name_en: string
          package_type_name_th: string
          package_id: number
          package_name: string
          quantity_sold: number
          total_sales_amount: number
        }
      >()

      for (const row of allRows) {
        if (!row.package_id || !row.package_type_id) {
          continue
        }

        const salesUserId = row.sales_user_id ?? ''
        const salesIdentifier =
          row.sales_user_id ?? row.sales_code ?? row.sales_full_name ?? 'UNASSIGNED'
        const key = `${salesIdentifier}-${row.package_type_id}-${row.package_id}`

        if (!grouped.has(key)) {
          grouped.set(key, {
            sales_user_id: salesUserId,
            sales_code: row.sales_code ?? '',
            sales_first_name: row.sales_first_name ?? '',
            sales_last_name: row.sales_last_name ?? '',
            sales_full_name: row.sales_full_name ?? '',
            package_type_id: row.package_type_id,
            package_type_name_en: row.package_type_name_en,
            package_type_name_th: row.package_type_name_th,
            package_id: row.package_id,
            package_name: row.package_name,
            quantity_sold: 0,
            total_sales_amount: 0,
          })
        }

        const current = grouped.get(key)!
        current.quantity_sold += row.quantity_sold
        current.total_sales_amount += row.total_sales_amount
      }

      const aggregated = Array.from(grouped.values())
        .sort((a, b) => b.total_sales_amount - a.total_sales_amount)
        .map((item) => ({
          ...item,
          package_price_thb: String(
            item.quantity_sold > 0 ? item.total_sales_amount / item.quantity_sold : 0
          ),
          total_unit_price_thb: String(item.total_sales_amount),
          avg_opportunity_percent: '0',
          expected_closing_value_thb: String(item.total_sales_amount),
          quantity_sold: String(item.quantity_sold),
          total_sales_amount: String(item.total_sales_amount),
        }))

      const start = Math.max(0, (page - 1) * pageSize)
      const end = start + pageSize

      return {
        data: aggregated.slice(start, end),
      }
    },

    async getFollowupStatus({
      year,
      month,
      page,
      pageSize,
    }: GetFollowupStatusParams): Promise<FollowupStatusResponse> {
      const query = new URLSearchParams()
      query.append(
        'select',
        [
          'sales_user_id',
          'sales_code',
          'sales_first_name',
          'sales_last_name',
          'sales_full_name',
          'lead_count',
          'prospect_count',
        ].join(',')
      )
      query.append('page', String(page))
      query.append('pageSize', String(pageSize))
      query.append('count', 'true')
      query.append('order', 'sales_full_name.asc')
      query.append('filter', `year_local=eq.${year}`)
      query.append('filter', `month_local=eq.${month}`)

      const { data } = await wrapApiCall(() =>
        client.get<PostgrestListResponse>(
          `${base}/lead_followup_status_by_sales_view?${query.toString()}`
        )
      )

      const rows = (data?.data ?? []).map((row) => ({
        sales_user_id: toNullableString(row.sales_user_id) ?? '',
        sales_code: toNullableString(row.sales_code) ?? '',
        sales_first_name: toNullableString(row.sales_first_name) ?? '',
        sales_last_name: toNullableString(row.sales_last_name) ?? '',
        sales_full_name: toNullableString(row.sales_full_name) ?? '',
        lead_count: String(toNumeric(row.lead_count)),
        prospect_count: String(toNumeric(row.prospect_count)),
      }))

      return {
        data: rows,
        pagination: data?.pagination ?? {
          total: rows.length,
          page,
          pageSize,
        },
      }
    },
  }
}
